import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import { isExpiredPresignedUrl } from '../utils/imageUrl';
import { formatBlogBody } from '../utils/blogFormatting';
import '../styles/Modal.css';
import '../styles/OperatorImageModal.css';

// dl.svg - shared download glyph for Download buttons/links
const DownloadIcon = ({ size = '1rem', className = '' }) => (
  <svg className={className} style={{ width: size, height: size, flexShrink: 0 }} viewBox="0 0 16 16" fill="none">
    <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" />
    <path d="M11.3346 6.66667L8.0013 10L4.66797 6.66667M8.0013 10V2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" />
  </svg>
);

const OperatorImageModal = ({
  isOpen,
  onClose,
  onConfirm,
  existingMediaUrl = null,
  existingOriginalUrl = null,
  existingCropData = null,
  existingBackgroundColor = null,
  brandName,
  brandLogo,
  draftTitle,
  draftBody,
  contentType,
  draftDate,
  allowUpload = true,
  showVersions = true
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(existingMediaUrl);
  const [originalImageUrl, setOriginalImageUrl] = useState(existingOriginalUrl || existingMediaUrl);
  const [crop, setCrop] = useState(existingCropData?.cropPosition || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(existingCropData?.zoom || 1);
  const [mediaSize, setMediaSize] = useState(null);
  const [cropAreaSize, setCropAreaSize] = useState(null);
  const [hasTransparency, setHasTransparency] = useState(!!existingBackgroundColor);
  const [backgroundColor, setBackgroundColor] = useState(existingBackgroundColor);
  const [isUploading, setIsUploading] = useState(false);
  const [imageDimensions, setImageDimensions] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    // The original may be an expired presigned S3 URL (older uploads). Fall back
    // to the permanent cropped media_url rather than showing a broken image.
    const usableOriginal = existingOriginalUrl && !isExpiredPresignedUrl(existingOriginalUrl)
      ? existingOriginalUrl
      : null;

    if (existingMediaUrl) {
      setImagePreviewUrl(existingMediaUrl);
      setOriginalImageUrl(usableOriginal || existingMediaUrl);

      // Load image dimensions for existing image
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = usableOriginal || existingMediaUrl;

      if (existingBackgroundColor) {
        setHasTransparency(true);
        setBackgroundColor(existingBackgroundColor);
      } else {
        detectImageTransparency(usableOriginal || existingMediaUrl).then((transparent) => {
          setHasTransparency(transparent);
          setBackgroundColor(transparent ? '#FFFFFF' : null);
        });
      }
    }
    if (existingCropData && usableOriginal) {
      setCrop(existingCropData.cropPosition || { x: 0, y: 0 });
      setZoom(existingCropData.zoom || 1);
    } else if (existingMediaUrl) {
      // Original is gone — crop coordinates were computed against it and no
      // longer apply to the fallback image, so start fresh instead of
      // applying a stale zoom/position to the wrong base image.
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [existingMediaUrl, existingOriginalUrl, existingCropData, existingBackgroundColor]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > 5) {
      alert(`File size (${sizeMB.toFixed(2)}MB) exceeds maximum (5MB for images)`);
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;

      // Load image and get dimensions
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
      };
      img.src = dataUrl;

      setImagePreviewUrl(dataUrl);
      setOriginalImageUrl(dataUrl);
      // Reset crop settings for new image
      setCrop({ x: 0, y: 0 });
      setZoom(1);

      detectImageTransparency(dataUrl).then((transparent) => {
        setHasTransparency(transparent);
        setBackgroundColor(transparent ? '#FFFFFF' : null);
      });
    };
    reader.readAsDataURL(file);
  };

  const getPreviewImageStyle = useCallback(() => {
    if (!imageDimensions) {
      return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '100%',
        maxHeight: '100%',
      };
    }

    const containerAspect = 4 / 3;
    const imageAspect = imageDimensions.width / imageDimensions.height;

    // The editor cropper is 450px wide × 337.5px tall
    const editorWidth = 450;
    const editorHeight = editorWidth / containerAspect; // 337.5

    // Convert pixel crop offsets to percentages of the editor container
    // These percentages will scale proportionally in any preview container size
    const cropPercentX = (crop.x / editorWidth) * 100;
    const cropPercentY = (crop.y / editorHeight) * 100;

    return {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: `translate(calc(-50% + ${cropPercentX}%), calc(-50% + ${cropPercentY}%)) scale(${zoom})`,
      transformOrigin: 'center',
      width: imageAspect > containerAspect ? 'auto' : '100%',
      height: imageAspect > containerAspect ? '100%' : 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
    };
  }, [imageDimensions, crop, zoom]);

  // For a freshly-selected file, originalImageUrl is a data: URL and can be
  // linked/downloaded directly. For an existing draft it's a cross-origin S3
  // URL — browsers ignore the `download` attribute for those and just
  // navigate to it instead (which opens the transparent PNG in the browser's
  // own image viewer, rendered against its white page background, rather
  // than saving the file) — so route it through our own origin instead,
  // where a Content-Disposition header forces a real save.
  const isRemoteOriginal = originalImageUrl && !originalImageUrl.startsWith('data:') && !originalImageUrl.startsWith('blob:');
  const originalDownloadHref = isRemoteOriginal
    ? `/api/content-drafts/media/download?url=${encodeURIComponent(originalImageUrl)}&filename=original-image.png`
    : originalImageUrl;

  const handleDownloadPositioned = async () => {
    if (!mediaSize || !cropAreaSize) return;

    try {
      const blob = await getCroppedImg(originalImageUrl, crop, zoom, mediaSize, cropAreaSize, 4 / 3, backgroundColor);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'positioned-image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error generating positioned image for download:', e);
      alert('Failed to generate image for download');
    }
  };

  const handleConfirm = async () => {
    if (!imagePreviewUrl) {
      alert('Please upload an image first');
      return;
    }

    try {
      setIsUploading(true);

      // Get the cropped image blob
      const croppedBlob = await getCroppedImg(originalImageUrl, crop, zoom, mediaSize, cropAreaSize, 4 / 3, backgroundColor);

      // Get original image blob
      let originalBlob = null;
      if (selectedFile) {
        originalBlob = selectedFile;
      } else if (existingOriginalUrl) {
        // If we're updating an existing image without changing it, don't re-upload original
        originalBlob = null;
      }

      // Pass both images and crop data to parent
      await onConfirm({
        croppedImage: croppedBlob,
        originalImage: originalBlob,
        cropData: { zoom, cropPosition: crop },
        backgroundColor
      });

      onClose();
    } catch (e) {
      console.error('Error processing image:', e);
      alert('Failed to process image');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay op-image-modal-overlay">
      <div className="modal-container op-image-modal">
        <div className="modal-header">
          <h3 className="modal-title">{allowUpload ? 'Upload & Position Image' : 'Review Image'}</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close" />
        </div>

        <div className="modal-body">
          {/* Hidden file input */}
          {allowUpload && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          )}

          <div className="op-image-editor-layout">
            {/* Image Editor Section */}
            <div className="op-image-editor-section">
              <div className="op-image-section-header">
                <h4>Position & Zoom Image</h4>
                {allowUpload && (
                  <button
                    className="op-image-replace-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Icon name="picture" size="0.875rem" />
                    {imagePreviewUrl ? 'Replace' : 'Upload Image'}
                  </button>
                )}
              </div>

              {imagePreviewUrl ? (
                <>
                  <div
                    className="op-image-crop-container"
                    style={backgroundColor && backgroundColor !== 'transparent' ? { backgroundColor } : undefined}
                  >
                    <Cropper
                      image={originalImageUrl}
                      crop={crop}
                      zoom={zoom}
                      minZoom={0.3}
                      maxZoom={3}
                      aspect={4 / 3}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      setMediaSize={setMediaSize}
                      setCropSize={setCropAreaSize}
                    />
                  </div>

                  <div className="op-image-crop-controls">
                    <label className="op-image-control-label">
                      <Icon name="search" size="1rem" />
                      <span>Zoom</span>
                    </label>
                    <input
                      type="range"
                      min={0.3}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="op-image-slider"
                    />
                    <span className="op-image-zoom-value">{zoom.toFixed(1)}x</span>
                  </div>

                  {hasTransparency && (
                    <div className="op-image-bg-controls">
                      <label className="op-image-control-label">
                        <Icon name="adjust" size="1rem" />
                        <span>Background</span>
                      </label>
                      <div className="op-image-bg-swatches">
                        <button
                          type="button"
                          className={`op-image-bg-swatch op-image-bg-swatch-none${backgroundColor === 'transparent' ? ' selected' : ''}`}
                          onClick={() => setBackgroundColor('transparent')}
                          title="No background (transparent)"
                        />
                        {['#FFFFFF', '#000000'].map((color) => (
                          <button
                            type="button"
                            key={color}
                            className={`op-image-bg-swatch${backgroundColor === color ? ' selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setBackgroundColor(color)}
                            title={color}
                          />
                        ))}
                        <button
                          type="button"
                          className={`op-image-bg-swatch op-image-bg-swatch-custom${backgroundColor && !['transparent', '#FFFFFF', '#000000'].includes(backgroundColor) ? ' selected' : ''}`}
                          style={backgroundColor && backgroundColor !== 'transparent' ? { backgroundColor } : undefined}
                          onClick={() => document.getElementById('op-image-bg-color-input').click()}
                          title="Custom color"
                        >
                          <Icon name="adjust" size="0.8rem" />
                        </button>
                        <input
                          id="op-image-bg-color-input"
                          type="color"
                          value={backgroundColor && backgroundColor !== 'transparent' ? backgroundColor : '#FFFFFF'}
                          onChange={(e) => setBackgroundColor(e.target.value)}
                          className="op-image-bg-color-input"
                        />
                      </div>
                      <span className="op-image-bg-value">
                        {backgroundColor === 'transparent' ? 'None' : (backgroundColor || '').toUpperCase()}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="op-image-upload-placeholder">
                  <Icon name="picture" size="3rem" />
                  <p>{allowUpload ? 'Click "Upload Image" to get started' : 'No image uploaded'}</p>
                </div>
              )}
            </div>

            {/* Preview Section */}
            <div className="op-image-preview-section">
              <h4>Post Preview</h4>

              <div className="op-image-preview-mock">
                {contentType === 'blog' ? (
                  <div className="mock-blog-post">
                    <div className="mock-blog-url-bar">
                      <span className="mock-blog-url">
                        {`https://blog.example.com/${(draftTitle || 'post').toLowerCase().replace(/\s+/g, '-')}`}
                      </span>
                      <span className="mock-type-tag mock-type-blog">BLOG</span>
                    </div>
                    {imagePreviewUrl ? (
                      <div
                        className="mock-blog-image-wrapper"
                        style={backgroundColor && backgroundColor !== 'transparent' ? { backgroundColor } : undefined}
                      >
                        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
                          <img
                            src={originalImageUrl}
                            alt="Preview"
                            style={getPreviewImageStyle()}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mock-blog-image-wrapper">
                        <div className="op-image-upload-placeholder">
                          <Icon name="picture" size="2rem" />
                          <p>Image will appear here</p>
                        </div>
                      </div>
                    )}
                    {draftTitle && <h2 className="mock-blog-title">{draftTitle}</h2>}
                    <div className="mock-blog-date">{draftDate || 'Just now'}</div>
                    <div className="mock-blog-body">
                      {formatBlogBody(draftBody || 'Your content will appear here with the image positioned as shown.').map(block => (
                        block.type === 'heading'
                          ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                          : <p key={block.key} className="mock-blog-paragraph">{block.text}</p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mock-social-post">
                    <div className="mock-post-header">
                      <div className="mock-author-info">
                        {brandLogo ? (
                          <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                        ) : (
                          <div className="mock-brand-avatar-placeholder">
                            {brandName ? brandName.charAt(0).toUpperCase() : 'B'}
                          </div>
                        )}
                        <div>
                          <div className="mock-brand-name">{brandName || 'Brand Name'}</div>
                          <div className="mock-post-meta">{draftDate || 'Just now'} · <Icon name="link" size="0.9rem" /></div>
                        </div>
                      </div>
                      <span className="mock-type-tag mock-type-social">SOCIAL</span>
                    </div>
                    {draftTitle && <div className="mock-post-title">{draftTitle}</div>}
                    <div className="mock-post-body">
                      {formatBlogBody(draftBody || 'Your content will appear here with the image positioned as shown.').map(block => (
                        block.type === 'heading'
                          ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                          : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                      ))}
                    </div>
                    {imagePreviewUrl ? (
                      <div
                        className="mock-media-wrapper"
                        style={backgroundColor && backgroundColor !== 'transparent' ? { backgroundColor } : undefined}
                      >
                        <div style={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          overflow: 'hidden'
                        }}>
                          <img
                            src={originalImageUrl}
                            alt="Preview"
                            style={getPreviewImageStyle()}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mock-media-wrapper">
                        <div className="op-image-upload-placeholder">
                          <Icon name="picture" size="2rem" />
                          <p>Image will appear here</p>
                        </div>
                      </div>
                    )}
                    <div className="mock-social-actions">
                      <span className="mock-social-btn" style={{ color: 'rgb(225, 48, 108)' }}><Icon name="heart" size="0.9rem" /> Like</span>
                      <span className="mock-social-btn"><Icon name="comment-empty" size="0.9rem" /> Comment</span>
                      <span className="mock-social-btn"><Icon name="share" size="0.9rem" /> Share</span>
                      <span className="mock-social-btn"><Icon name="paper-plane-empty" size="0.9rem" /> Send</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Image Versions Display */}
          {showVersions && (
            <div className="op-image-versions-section">
              <h4>Image Versions</h4>
              <div className="op-image-versions-grid">
                <div className="op-image-version-card">
                  <div className="op-image-version-label">
                    <span>Original Image</span>
                    {imagePreviewUrl && (
                      <a
                        href={originalDownloadHref}
                        download="original-image.png"
                        className="op-image-download-link"
                        title="Download original image"
                      >
                        <DownloadIcon size="0.9rem" /> Download
                      </a>
                    )}
                  </div>
                  <div className="op-image-version-preview">
                    {imagePreviewUrl ? (
                      <img src={originalImageUrl} alt="Original" />
                    ) : (
                      <div className="op-image-upload-placeholder">
                        <Icon name="picture" size="1.5rem" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="op-image-version-card">
                  <div className="op-image-version-label">
                    <span>Positioned for Post</span>
                    {imagePreviewUrl && mediaSize && cropAreaSize && (
                      <button
                        type="button"
                        onClick={handleDownloadPositioned}
                        className="op-image-download-link"
                        title="Download positioned image"
                      >
                        <DownloadIcon size="0.9rem" /> Download
                      </button>
                    )}
                  </div>
                  <div
                    className="op-image-version-preview op-image-cropped-preview"
                    style={backgroundColor && backgroundColor !== 'transparent' ? { backgroundColor } : undefined}
                  >
                    {imagePreviewUrl ? (
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden'
                      }}>
                        <img
                          src={originalImageUrl}
                          alt="Cropped Preview"
                          style={getPreviewImageStyle()}
                        />
                      </div>
                    ) : (
                      <div className="op-image-upload-placeholder">
                        <Icon name="picture" size="1.5rem" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="modal-btn modal-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="modal-btn modal-btn-confirm op-image-btn-confirm"
            onClick={handleConfirm}
            disabled={!imagePreviewUrl || isUploading}
          >
            <Icon name="check" size="1rem" />
            {isUploading ? 'Uploading...' : (allowUpload ? 'Confirm & Upload' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// S3 URLs are a different origin than the app, so loading one into an <img>
// and then reading it back via canvas (getImageData/toBlob) throws a
// SecurityError regardless of the bucket's CORS config unless the browser
// negotiated CORS for that specific load. Rather than depend on that, route
// non-same-origin sources through our own backend, which fetches the S3
// bytes server-to-server and hands them back same-origin.
const getCorsSafeImageUrl = async (imageSrc) => {
  if (!imageSrc || imageSrc.startsWith('data:') || imageSrc.startsWith('blob:')) {
    return imageSrc;
  }

  try {
    const parsed = new URL(imageSrc, window.location.href);
    if (parsed.origin === window.location.origin) {
      return imageSrc;
    }
  } catch (e) {
    return imageSrc;
  }

  const response = await fetch(`/api/content-drafts/media/download?url=${encodeURIComponent(imageSrc)}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to load image');
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

// Loads an image and scans a downscaled copy for any non-opaque pixel.
const detectImageTransparency = async (imageSrc) => {
  let safeSrc;
  try {
    safeSrc = await getCorsSafeImageUrl(imageSrc);
  } catch (e) {
    return false;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const size = 50;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            resolve(true);
            return;
          }
        }
        resolve(false);
      } catch (e) {
        resolve(false);
      } finally {
        if (safeSrc !== imageSrc) URL.revokeObjectURL(safeSrc);
      }
    };
    img.onerror = () => resolve(false);
    img.src = safeSrc;
  });
};

// Mirrors react-easy-crop's own internal crop-rectangle math, but without its
// clamp to the image's own bounds. The library clamps the exported crop area
// to a maximum of the full image any time zoom < 1 (i.e. whenever the image
// is smaller than the crop frame) — which is exactly what this feature relies
// on when an operator zooms out to show background padding around a
// transparent image — silently discarding the zoom-out/position and always
// exporting the full, unzoomed image instead. Computing it ourselves keeps it
// accurate at any zoom, including <1; the resulting x/y/width/height can then
// legitimately fall outside the source image, which canvas drawImage handles
// correctly by drawing only the overlapping region at the right place.
const computeCropPixels = (crop, mediaSize, cropSize, aspect, zoom) => {
  const croppedAreaPercentages = {
    x: ((mediaSize.width - cropSize.width / zoom) / 2 - crop.x / zoom) / mediaSize.width * 100,
    y: ((mediaSize.height - cropSize.height / zoom) / 2 - crop.y / zoom) / mediaSize.height * 100,
    width: cropSize.width / mediaSize.width * 100 / zoom,
    height: cropSize.height / mediaSize.height * 100 / zoom
  };

  const widthInPixels = croppedAreaPercentages.width * mediaSize.naturalWidth / 100;
  const heightInPixels = croppedAreaPercentages.height * mediaSize.naturalHeight / 100;
  const sizePixels = mediaSize.naturalWidth >= mediaSize.naturalHeight * aspect
    ? { width: heightInPixels * aspect, height: heightInPixels }
    : { width: widthInPixels, height: widthInPixels / aspect };

  return {
    width: sizePixels.width,
    height: sizePixels.height,
    x: croppedAreaPercentages.x * mediaSize.naturalWidth / 100,
    y: croppedAreaPercentages.y * mediaSize.naturalHeight / 100
  };
};

const getCroppedImg = async (imageSrc, crop, zoom, mediaSize, cropSize, aspectRatio, backgroundColor) => {
  const safeSrc = await getCorsSafeImageUrl(imageSrc);

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = safeSrc;
    image.onload = () => {
      const pixels = computeCropPixels(crop, mediaSize, cropSize, aspectRatio, zoom);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set output canvas to a reasonable size (max 1200 width)
      const maxOutputWidth = 1200;
      const outputWidth = Math.min(maxOutputWidth, Math.round(pixels.width));
      const outputHeight = Math.round(outputWidth / aspectRatio);

      canvas.width = outputWidth;
      canvas.height = outputHeight;

      // Fill with the chosen background before drawing so both true alpha
      // transparency and any empty space left by zooming out show this color
      // instead of the browser's default transparent/black canvas fill.
      if (backgroundColor && backgroundColor !== 'transparent') {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // pixels gives the exact (possibly out-of-bounds) source rectangle;
      // drawImage only paints the part that overlaps the actual image.
      ctx.drawImage(
        image,
        pixels.x,
        pixels.y,
        pixels.width,
        pixels.height,
        0,
        0,
        canvas.width,
        canvas.height
      );

      if (safeSrc !== imageSrc) URL.revokeObjectURL(safeSrc);

      // Always save as PNG to preserve transparency
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/png', 1.0);
    };
    image.onerror = reject;
  });
};

export default OperatorImageModal;
